import { AnimatePresence, motion } from 'motion/react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { Text, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { useAutoResizeTextarea } from '@/components/ui/form/useAutoResizeTextArea';
import { XStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import { useKGAgentSession } from './useKGAgentSession';

import type { KGAgentToolCall } from './world-model.types';

const STORAGE_KEY = 'world-model-chat-sidebar-width';
const DRAFT_STORAGE_KEY = 'world-model-chat-draft';
const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 280;
const MAX_WIDTH = 600;

type ToolDisplayConfig = {
  label: string;
  icon: ReactNode;
  formatArgs: (args: Record<string, unknown>) => string;
};

function formatGenericArgs(args: Record<string, unknown>): string {
  const entries = Object.entries(args);
  if (entries.length === 0) return '';
  if (entries.length === 1) {
    const [, value] = entries[0];
    return typeof value === 'string' ? `"${value}"` : String(value);
  }
  return entries
    .slice(0, 2)
    .map(
      ([key, value]) =>
        `${key}: ${typeof value === 'string' ? `"${value}"` : value}`
    )
    .join(', ');
}

const TOOL_DISPLAY_MAP: Record<string, ToolDisplayConfig> = {
  search_entities: {
    label: 'Searched entities',
    icon: <Icons.search className="size-3.5" />,
    formatArgs: (args) => (args.query ? `for "${args.query}"` : ''),
  },
  get_entity: {
    label: 'Retrieved entity',
    icon: <Icons.www className="size-3.5" />,
    formatArgs: (args) =>
      args.name ? `"${args.name}"` : args.entity_id ? `#${args.entity_id}` : '',
  },
  get_valid_relationships: {
    label: 'Retrieved relationships',
    icon: <Icons.www className="size-3.5" />,
    formatArgs: (args) =>
      args.entity_id ? `for entity #${args.entity_id}` : '',
  },
  get_immediate_network: {
    label: 'Retrieved network',
    icon: <Icons.www className="size-3.5" />,
    formatArgs: (args) =>
      args.entity_id ? `for entity #${args.entity_id}` : '',
  },
  create_entity: {
    label: 'Created entity',
    icon: <Icons.plus className="size-3.5" />,
    formatArgs: (args) => (args.name ? `"${args.name}"` : ''),
  },
  update_entity: {
    label: 'Updated entity',
    icon: <Icons.edit className="size-3.5" />,
    formatArgs: (args) => (args.entity_id ? `#${args.entity_id}` : ''),
  },
  create_relationship: {
    label: 'Created relationship',
    icon: <Icons.plus className="size-3.5" />,
    formatArgs: formatGenericArgs,
  },
  update_relationship: {
    label: 'Updated relationship',
    icon: <Icons.edit className="size-3.5" />,
    formatArgs: (args) =>
      args.relationship_id ? `#${args.relationship_id}` : '',
  },
};

function getToolDisplay(name: string): ToolDisplayConfig {
  return (
    TOOL_DISPLAY_MAP[name] ?? {
      label: name.replace(/_/g, ' '),
      icon: <Icons.www className="size-3.5" />,
      formatArgs: formatGenericArgs,
    }
  );
}

function ToolCallCard({ toolCall }: { toolCall: KGAgentToolCall }) {
  const [isOpen, setIsOpen] = useState(false);
  const display = getToolDisplay(toolCall.name);
  const argsSummary = display.formatArgs(toolCall.arguments);
  const toggleOpen = () => {
    if (window.getSelection()?.toString()) return;
    setIsOpen(!isOpen);
  };

  return (
    <div className="py-1">
      <XStack
        className="items-center gap-2 text-foreground-subtle cursor-pointer group"
        onClick={toggleOpen}
      >
        <span className="shrink-0 text-foreground-subtle group-hover:text-foreground transition-colors">
          {isOpen ? (
            <Icons.chevronDown className="size-3.5" />
          ) : (
            <Icons.chevronRight className="size-3.5" />
          )}
        </span>
        <span className="shrink-0">{display.icon}</span>
        <span className="text-sm select-text">
          {display.label}
          {argsSummary && (
            <span className="text-foreground-muted ml-1">{argsSummary}</span>
          )}
        </span>
      </XStack>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-2 p-3 rounded-lg bg-black/20 text-xs text-foreground-muted">
              <pre className="whitespace-pre-wrap font-mono max-h-40 overflow-y-auto select-text">
                {toolCall.result}
              </pre>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ReasoningBlurb({ thought }: { thought: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => {
    if (window.getSelection()?.toString()) return;
    setIsOpen(!isOpen);
  };

  return (
    <div className="py-1">
      <XStack
        className="items-center gap-2 text-foreground-subtle cursor-pointer group"
        onClick={toggleOpen}
      >
        <span className="shrink-0 text-foreground-subtle group-hover:text-foreground transition-colors">
          {isOpen ? (
            <Icons.chevronDown className="size-3.5" />
          ) : (
            <Icons.chevronRight className="size-3.5" />
          )}
        </span>
        <span className="text-xs uppercase tracking-wide text-foreground-muted">
          Reasoning
        </span>
      </XStack>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-2 rounded-lg bg-black/20 p-3">
              <span className="whitespace-pre-wrap text-xs text-foreground-muted select-text">
                {thought}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface KGAgentChatSidebarProps {
  onClose: () => void;
}

export function KGAgentChatSidebar({ onClose }: KGAgentChatSidebarProps) {
  const { messages, isStreaming, sendMessage, stopStreaming, clearSession } =
    useKGAgentSession();
  const [input, setInput] = useState(() => {
    const stored = getLocalStorageItem(DRAFT_STORAGE_KEY);
    return typeof stored === 'string' ? stored : '';
  });
  const [confirmClear, setConfirmClear] = useState(false);
  const [isHoveringSubmit, setIsHoveringSubmit] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useAutoResizeTextarea<HTMLTextAreaElement>(input);

  // Persist draft input
  useEffect(() => {
    setLocalStorageItem(DRAFT_STORAGE_KEY, input);
  }, [input]);

  // Reset confirm state after timeout
  useEffect(() => {
    if (confirmClear) {
      const timer = setTimeout(() => setConfirmClear(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [confirmClear]);

  const handleClearClick = useCallback(() => {
    if (confirmClear) {
      clearSession();
      setConfirmClear(false);
    } else {
      setConfirmClear(true);
    }
  }, [confirmClear, clearSession]);

  const [width, setWidth] = useState(() => {
    const stored = getLocalStorageItem(STORAGE_KEY);
    return typeof stored === 'number' ? stored : DEFAULT_WIDTH;
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!isDragging) {
      setLocalStorageItem(STORAGE_KEY, width);
    }
  }, [width, isDragging]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setIsDragging(true);
      dragStartX.current = event.clientX;
      dragStartWidth.current = width;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = dragStartX.current - moveEvent.clientX;
        const newWidth = Math.min(
          MAX_WIDTH,
          Math.max(MIN_WIDTH, dragStartWidth.current + deltaX)
        );
        setWidth(newWidth);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
      };

      document.body.style.cursor = 'ew-resize';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [width]
  );

  const handleSubmit = useCallback(() => {
    if (!input.trim() || isStreaming) return;
    sendMessage(input);
    setInput('');
  }, [input, isStreaming, sendMessage]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <div
      className="h-full border-l border-border flex flex-col relative"
      style={{ width }}
    >
      {/* Left resize handle */}
      <div
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10',
          'hover:bg-border-strongest transition-colors',
          isDragging && 'bg-border-strongest'
        )}
        onMouseDown={handleMouseDown}
      />
      {/* Header */}
      <XStack className="items-center justify-between px-4 py-2 border-b border-border">
        <Text size="sm" weight="medium" className="text-foreground-muted">
          World Model Chat
        </Text>
        <XStack className="gap-1">
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClearClick}
              onBlur={() => setConfirmClear(false)}
              title={confirmClear ? 'Click again to confirm' : 'Clear chat'}
            >
              {confirmClear ? (
                <Icons.check className="text-danger-9" />
              ) : (
                <Icons.trash />
              )}
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onClose} title="Close">
            <Icons.cancel />
          </Button>
        </XStack>
      </XStack>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
        {messages.length === 0 ? (
          <div className="text-sm text-foreground-muted text-center py-8">
            Ask questions about your world model or make updates.
          </div>
        ) : (
          messages.map((message, index) => {
            const isFirst = index === 0;
            const followsUser = messages[index - 1]?.role === 'user';
            return message.role === 'user' ? (
              <div
                key={index}
                className={cn('flex justify-end', !isFirst && 'mt-4')}
              >
                <div className="text-sm rounded-lg px-3 py-2 bg-surface-hover text-accent max-w-[85%] w-fit">
                  <span className="whitespace-pre-wrap select-text">
                    {message.content}
                  </span>
                </div>
              </div>
            ) : (
              <YStack
                key={index}
                className={cn(
                  'gap-1 text-sm text-foreground max-w-[90%]',
                  followsUser ? 'mt-6' : 'mt-4'
                )}
              >
                {message.blocks.map((block, blockIndex) => {
                  if (block.type === 'tool_call') {
                    return (
                      <ToolCallCard
                        key={`${index}-tool-${blockIndex}`}
                        toolCall={block.toolCall}
                      />
                    );
                  }

                  if (block.type === 'thought') {
                    return (
                      <ReasoningBlurb
                        key={`${index}-thought-${blockIndex}`}
                        thought={block.content}
                      />
                    );
                  }

                  if (block.type === 'interrupted') {
                    return (
                      <XStack
                        key={`${index}-interrupted-${blockIndex}`}
                        className="mt-2 items-center gap-1.5 text-xs text-foreground-muted"
                      >
                        <Icons.stop className="size-3 shrink-0" />
                        <span>Stopped</span>
                      </XStack>
                    );
                  }

                  return (
                    <div key={`${index}-text-${blockIndex}`} className="mt-3">
                      <span className="whitespace-pre-wrap text-foreground-subtle text-sm select-text">
                        {block.content}
                      </span>
                    </div>
                  );
                })}
              </YStack>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-border">
        <XStack className="gap-2 items-end">
          <div
            className={cn(
              'flex-1 flex items-center gap-2 rounded-lg border border-border-strong bg-surface px-3 py-2',
              'focus-within:border-accent/60'
            )}
          >
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your world model..."
              className={cn(
                'flex-1 min-h-12 max-h-32 resize-none bg-transparent text-sm leading-5 placeholder:text-foreground-muted focus:outline-none',
                input.length > 0 ? 'overflow-y-auto' : 'overflow-hidden'
              )}
              rows={1}
              disabled={isStreaming}
            />
          </div>
          <Button
            variant="default"
            size="icon"
            onClick={isStreaming ? stopStreaming : handleSubmit}
            disabled={!isStreaming && !input.trim()}
            className="self-end"
            title={isStreaming ? 'Stop generation' : 'Send message'}
            onPointerEnter={(event) => {
              // hover only previews the stop icon; a touch tap must not swap
              // visible content or WebKit withholds the click
              if (event.pointerType !== 'mouse') return;
              setIsHoveringSubmit(true);
            }}
            onPointerLeave={(event) => {
              if (event.pointerType !== 'mouse') return;
              setIsHoveringSubmit(false);
            }}
          >
            {isStreaming ? (
              isHoveringSubmit ? (
                <Icons.stop className="size-4" />
              ) : (
                <Icons.loader className="animate-spin" />
              )
            ) : (
              <Icons.send />
            )}
          </Button>
        </XStack>
      </div>
    </div>
  );
}
